/**
 * V5 Code Editor Component
 * Monaco-based code editor with Tailwind styling for coding simulations
 */

import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Play, Upload, ChevronDown, ChevronUp, 
    FileCode, CheckCircle, XCircle, Clock, Loader2, Terminal
} from 'lucide-react';
import clsx from 'clsx';
import { CodingTask, CodeExecutionResult } from '../types';

const cleanPistonPaths = (text: string): string => {
    return text.replace(/\/piston\/jobs\/[a-f0-9-]+\//g, '');
};

interface CodeEditorProps {
    task: CodingTask;
    sessionId: string;
    onRun: (code: string) => Promise<CodeExecutionResult>;
    onPush: (code: string) => Promise<{ success: boolean; message: string }>;
    isRunning: boolean;
    isPushing: boolean;
    lastResult?: CodeExecutionResult;
}

export function CodeEditor({
    task,
    sessionId,
    onRun,
    onPush,
    isRunning,
    isPushing,
    lastResult
}: CodeEditorProps) {
    const [code, setCode] = useState(task.starter_code || '');
    const [showDescription, setShowDescription] = useState(true);
    const [showConstraints, setShowConstraints] = useState(false);
    const [showResults, setShowResults] = useState(true);
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const editorRef = useRef<any>(null);

    // Reset code when task changes
    useEffect(() => {
        setCode(task.starter_code || '');
        setShowResults(true);
        setNotification(null);
    }, [task.task_id]);

    const handleEditorDidMount = (editor: any, monaco: any) => {
        editorRef.current = editor;
        
        // Disable paste command (Ctrl+V / Cmd+V)
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV, () => {
            // Do nothing - paste disabled
        });
        
        // Also intercept any paste events and undo them
        editor.onDidPaste(() => {
            editor.trigger('keyboard', 'undo', null);
        });
    };

    const showNotification = (type: 'success' | 'error', message: string) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 4000);
    };

    const handleRun = async () => {
        try {
            const result = await onRun(code);
            // Derive success from exit_code if success field is not set
            const isSuccess = result.success ?? (result.exit_code === 0);
            
            if (isSuccess) {
                showNotification('success', `Code executed successfully (Exit code: ${result.exit_code})`);
            } else {
                showNotification('error', `Execution failed (Exit code: ${result.exit_code})`);
            }
            // Auto-expand results when execution completes
            setShowResults(true);
        } catch (error) {
            showNotification('error', error instanceof Error ? error.message : 'Failed to execute code');
        }
    };

    const handlePush = async () => {
        try {
            const result = await onPush(code);
            if (result.success) {
                showNotification('success', 'Code pushed to upstream successfully');
            } else {
                showNotification('error', result.message);
            }
        } catch (error) {
            showNotification('error', error instanceof Error ? error.message : 'Failed to push code');
        }
    };

    const getLanguageForMonaco = (language: string): string => {
        const languageMap: Record<string, string> = {
            'python': 'python',
            'javascript': 'javascript',
            'typescript': 'typescript',
            'java': 'java',
            'cpp': 'cpp',
            'c': 'c',
            'go': 'go',
            'rust': 'rust',
            'ruby': 'ruby',
            'php': 'php',
            'csharp': 'csharp',
        };
        return languageMap[language?.toLowerCase()] || 'plaintext';
    };

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Notification */}
            <AnimatePresence>
                {notification && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className={clsx(
                            'fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2',
                            notification.type === 'success' 
                                ? 'bg-green-50 border border-green-200 text-green-800'
                                : 'bg-red-50 border border-red-200 text-red-800'
                        )}
                    >
                        {notification.type === 'success' 
                            ? <CheckCircle className="w-5 h-5 text-green-600" />
                            : <XCircle className="w-5 h-5 text-red-600" />
                        }
                        <span className="text-sm font-medium">{notification.message}</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Task Header */}
            <div className="p-4 border-b border-slate-200 bg-white flex-shrink-0 max-h-[250px] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <FileCode className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">{task.title}</h2>
                            <div className="flex items-center gap-2 mt-0.5">
                                {task.language && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                        {task.language}
                                    </span>
                                )}
                                {task.task_type && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                                        {task.task_type}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Collapsible Description */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                        onClick={() => setShowDescription(!showDescription)}
                        className="w-full px-3 py-2 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
                    >
                        <span className="text-sm font-semibold text-slate-700">Description</span>
                        {showDescription ? (
                            <ChevronUp className="w-4 h-4 text-slate-500" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                        )}
                    </button>
                    <AnimatePresence>
                        {showDescription && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                            >
                                <p className="px-3 py-2 text-sm text-slate-600 whitespace-pre-wrap">
                                    {task.description}
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Collapsible Constraints */}
                {task.constraints && task.constraints.length > 0 && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden mt-2">
                        <button
                            onClick={() => setShowConstraints(!showConstraints)}
                            className="w-full px-3 py-2 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
                        >
                            <span className="text-sm font-semibold text-slate-700">Constraints</span>
                            {showConstraints ? (
                                <ChevronUp className="w-4 h-4 text-slate-500" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                            )}
                        </button>
                        <AnimatePresence>
                            {showConstraints && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                >
                                    <ul className="px-3 py-2 space-y-1">
                                        {task.constraints.map((constraint, idx) => (
                                            <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                                                <span className="text-emerald-500 mt-1">•</span>
                                                {constraint}
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Monaco Editor */}
            <div className="flex-1 min-h-[200px]">
                <Editor
                    height="100%"
                    language={getLanguageForMonaco(task.language || 'python')}
                    value={code}
                    onChange={(value) => setCode(value || '')}
                    onMount={handleEditorDidMount}
                    theme="vs-dark"
                    options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        wordWrap: 'on',
                        padding: { top: 12, bottom: 12 },
                    }}
                />
            </div>

            {/* Action Buttons */}
            <div className="p-2 bg-white border-t border-slate-200 flex items-center justify-end gap-3 flex-shrink-0">
                <button
                    onClick={handleRun}
                    disabled={isRunning || isPushing}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {isRunning ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Play className="w-4 h-4" />
                    )}
                    {isRunning ? 'Running...' : 'Run'}
                </button>
                <button
                    onClick={handlePush}
                    disabled={isRunning || isPushing}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {isPushing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Upload className="w-4 h-4" />
                    )}
                    {isPushing ? 'Pushing...' : 'Push to Upstream'}
                </button>
            </div>
            </div>

            {/* Execution Results - Fixed Bottom Panel */}
            <div className="flex-shrink-0 border-t border-slate-300 bg-slate-800 text-white">
                <button
                    onClick={() => setShowResults(!showResults)}
                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-slate-700 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Terminal className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-semibold text-slate-200">Output</span>
                        {lastResult && (() => {
                            const isSuccess = lastResult.success ?? (lastResult.exit_code === 0);
                            return (
                                <span className={clsx(
                                    'px-2 py-0.5 text-xs font-semibold rounded',
                                    isSuccess 
                                        ? 'bg-green-600 text-green-100' 
                                        : 'bg-red-600 text-red-100'
                                )}>
                                    Exit: {lastResult.exit_code}
                                </span>
                            );
                        })()}
                    </div>
                    {showResults ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                    )}
                </button>
                
                <AnimatePresence>
                    {showResults && (
                        <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 200 }}
                            exit={{ height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <div className="h-[200px] overflow-y-auto p-4 space-y-3 bg-slate-900">
                                {lastResult ? (
                                    <>
                                        {/* Standard Output */}
                                        <div>
                                            <p className="text-xs font-semibold text-slate-400 mb-1">stdout:</p>
                                            <pre className="p-2 bg-slate-800 rounded text-sm text-slate-200 overflow-x-auto whitespace-pre-wrap font-mono">
                                                {lastResult.stdout ? cleanPistonPaths(lastResult.stdout) : '(no output)'}
                                            </pre>
                                        </div>
                                        
                                        {/* Standard Error */}
                                        {lastResult.stderr && lastResult.stderr.trim() !== '' && (
                                            <div>
                                                <p className="text-xs font-semibold text-red-400 mb-1">stderr:</p>
                                                <pre className="p-2 bg-red-900/30 border border-red-800 rounded text-sm text-red-300 overflow-x-auto whitespace-pre-wrap font-mono">
                                                    {cleanPistonPaths(lastResult.stderr)}
                                                </pre>
                                            </div>
                                        )}
                                        
                                        {/* Execution Info */}
                                        <div className="flex items-center gap-4 pt-2 border-t border-slate-700">
                                            <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                <Clock className="w-3.5 h-3.5" />
                                                <span>{lastResult.runtime_ms}ms</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                <FileCode className="w-3.5 h-3.5" />
                                                <span>{lastResult.language}</span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-slate-500 text-sm">
                                        Run your code to see output here
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

export default CodeEditor;

